// Shared utility functions

export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'Invalid date';
  }
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const generateId = (): string => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

// Derive initials from a name (e.g., "Universal Music Group" -> "UM")
export const getInitials = (name: string, maxLetters: number = 2): string => {
  const parts = (name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, maxLetters).toUpperCase();
  const initials = parts[0][0] + parts[parts.length - 1][0];
  return initials.toUpperCase();
};

// --- Input helpers ---
// Normalize a website string by trimming and adding https:// if protocol is missing
export const normalizeWebsite = (raw: string | undefined | null): string => {
  const val = (raw || '').trim();
  if (!val) return '';
  // If it already starts with a scheme, return as-is
  if (/^https?:\/\//i.test(val)) return val;
  // If the user typed something like www.example.com, prepend https://
  return `https://${val}`;
};

// Format a US phone number as the user types, e.g., (310) 555-1234
export const formatPhone = (raw: string | undefined | null): string => {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 15); // allow up to 15 digits
  // Simple US-centric prettifier for 10 digits; otherwise group in chunks
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  // For longer numbers, just return spaced groups after first 10
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)} ${digits.slice(10)}`;
};

// Normalize phone for storage (digits only). Consider E.164 in future if needed
export const normalizePhone = (raw: string | undefined | null): string => {
  return (raw || '').replace(/\D/g, '');
};

// Suggest a correction for common email mistakes (non-blocking UX hint)
export type EmailSuggestion = { suggestion: string; reason: string };

export const suggestEmailCorrection = (raw: string | undefined | null): EmailSuggestion | null => {
  const email = (raw || '').trim();
  if (!email) return null;
  const atIdx = email.indexOf('@');
  if (atIdx === -1) return null; // not an email-like input

  const local = email.slice(0, atIdx);
  const domainRaw = email.slice(atIdx + 1);
  if (!local || !domainRaw) return null;

  // normalize domain case and dots
  const domainLower = domainRaw.toLowerCase().replace(/\.+/g, '.').replace(/^\.|\.$/g, '');

  // Map common domain typos to correct ones
  const commonDomains: Record<string, string> = {
    // Gmail
    'gmail.co': 'gmail.com',
    'gmal.com': 'gmail.com',
    'gmial.com': 'gmail.com',
    'gmaill.com': 'gmail.com',
    // Outlook
    'oulook.com': 'outlook.com',
    'outlok.com': 'outlook.com',
    'outlook.co': 'outlook.com',
    // Hotmail
    'hotnail.com': 'hotmail.com',
    'hotmial.com': 'hotmail.com',
    'hotmail.co': 'hotmail.com',
    // Yahoo
    'yaho.com': 'yahoo.com',
    'yhoo.com': 'yahoo.com',
    'yahoo.co': 'yahoo.com',
    // iCloud
    'icloud.con': 'icloud.com',
    'iclod.com': 'icloud.com',
    // ProtonMail
    'protonmal.com': 'protonmail.com',
  };

  // Common TLD typos
  const tldTypos: Record<string, string> = {
    '.con': '.com',
    '.cmo': '.com',
    '.xom': '.com',
  };

  // Exact domain typo corrections
  if (commonDomains[domainLower]) {
    const corrected = `${local}@${commonDomains[domainLower]}`;
    if (corrected !== email) return { suggestion: corrected, reason: 'Corrected common email domain typo' };
  }

  // Correct TLD typos at the end
  for (const [bad, good] of Object.entries(tldTypos)) {
    if (domainLower.endsWith(bad)) {
      const correctedDomain = domainLower.slice(0, -bad.length) + good;
      const corrected = `${local}@${correctedDomain}`;
      if (corrected !== email) return { suggestion: corrected, reason: `Fixed TLD typo (${bad} → ${good})` };
    }
  }

  // Missing dot in domain (likely missing TLD) → add .com
  if (!domainLower.includes('.')) {
    const corrected = `${local}@${domainLower}.com`;
    if (corrected !== email) return { suggestion: corrected, reason: 'Added missing .com TLD' };
  }

  // Double dots or leading/trailing dots fixed by normalization earlier
  const normalizedDomain = domainLower;
  const normalized = `${local}@${normalizedDomain}`;
  if (normalized !== email) {
    return { suggestion: normalized, reason: 'Cleaned up domain formatting' };
  }

  return null;
};

// --- Validation helpers for badges ---
export const isValidEmailSyntax = (raw: string | undefined | null): boolean => {
  const email = (raw || '').trim();
  if (!email) return true; // empty not invalid for optional fields
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const getEmailDomain = (raw: string | undefined | null): string | null => {
  const email = (raw || '').trim();
  const at = email.indexOf('@');
  if (at === -1) return null;
  return email.slice(at + 1).toLowerCase();
};

const DISPOSABLE_DOMAINS = new Set([
  'mailinator.com','10minutemail.com','guerrillamail.com','tempmail.com','yopmail.com','trashmail.com'
]);

export const isDisposableDomain = (domain: string | null): boolean => {
  if (!domain) return false;
  return DISPOSABLE_DOMAINS.has(domain);
};

export const getHostname = (url: string | undefined | null): string | null => {
  const val = (url || '').trim();
  if (!val) return null;
  try {
    const u = new URL(normalizeWebsite(val));
    return u.hostname.toLowerCase();
  } catch {
    return null;
  }
};

export const domainsMatch = (a?: string | null, b?: string | null): boolean => {
  const da = (a || '').toLowerCase();
  const db = (b || '').toLowerCase();
  if (!da || !db) return false;
  return da === db || da.endsWith('.' + db) || db.endsWith('.' + da);
};